import { shopifyImageSrcset, shopifyImageUrl } from '../utils/shopify-image'

const AUCTION_PROXY_URL = '/eshop/auctions'
const SHOP_DOMAIN = 'shop.lausannehc.ch'

const STOREFRONT_TOKEN = '7bf999e29b526048328f478173fd6100'
const STOREFRONT_API_VERSION = '2026-07'

const BADGE_TAG_PREFIX = 'badge:'

const IMAGE_WIDTHS = [400, 700, 1000, 1300, 1600, 2000]

const CURRENCY = 'CHF'
const COUNTDOWN_INTERVAL = 1000

type AuctionApiItem = {
  shopify_product_id: string
  shopify_product_title: string
  shopify_featured_image: string | null
  shopify_product_url: string
  starting_price: string
  highest_bid: string | null
  buy_it_now_price: string | null
  bid_count: number
  end_date: string
}

type Auction = {
  title: string
  imageUrl: string | null
  imageSrcset: string | null
  productUrl: string
  currentBid: string
  bidCount: number
  endsAt: number
  badges: string[]
}

function formatPrice(amount: number): string {
  const formatted = new Intl.NumberFormat('fr-CH', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount)

  return `${CURRENCY} ${formatted}`
}

function formatCountdown(remainingMs: number): string {
  const totalSeconds = Math.max(0, Math.floor(remainingMs / 1000))
  const days = Math.floor(totalSeconds / 86400)
  const hours = Math.floor((totalSeconds % 86400) / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  const pad = (value: number) => String(value).padStart(2, '0')

  return days > 0
    ? `${days}j ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
    : `${hours}:${pad(minutes)}:${pad(seconds)}`
}

function currentAmount(item: AuctionApiItem): number {
  return Number(item.bid_count > 0 && item.highest_bid ? item.highest_bid : item.starting_price)
}

function extractBadges(tags: string[]): string[] {
  return tags
    .filter((tag) => tag.toLowerCase().startsWith(BADGE_TAG_PREFIX))
    .map((tag) => tag.slice(BADGE_TAG_PREFIX.length).trim())
    .filter(Boolean)
}

function toAuction(item: AuctionApiItem, tags: string[]): Auction {
  const image = item.shopify_featured_image

  return {
    title: item.shopify_product_title,
    imageUrl: image && shopifyImageUrl(image, 1000),
    imageSrcset: image && shopifyImageSrcset(image, IMAGE_WIDTHS),
    productUrl: `https://${SHOP_DOMAIN}${item.shopify_product_url}`,
    currentBid: formatPrice(currentAmount(item)),
    bidCount: item.bid_count,
    endsAt: new Date(item.end_date).getTime(),
    badges: extractBadges(tags),
  }
}

export async function fetchAuctionsFromProxy(): Promise<AuctionApiItem[]> {
  const response = await fetch(AUCTION_PROXY_URL)

  if (!response.ok) {
    throw new Error(`Auction proxy error: ${response.status}`)
  }

  return response.json()
}

const PRODUCT_TAGS_QUERY = `
  query AuctionProductTags($id: ID!) {
    product(id: $id) {
      tags
    }
  }
`

export async function fetchProductTagsFromStorefront(productId: string): Promise<string[]> {
  const response = await fetch(`https://${SHOP_DOMAIN}/api/${STOREFRONT_API_VERSION}/graphql.json`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN,
    },
    body: JSON.stringify({
      query: PRODUCT_TAGS_QUERY,
      variables: { id: `gid://shopify/Product/${productId}` },
    }),
  })

  if (!response.ok) {
    throw new Error(`Shopify Storefront API error: ${response.status}`)
  }

  const { data, errors } = await response.json()

  if (errors?.length) {
    throw new Error(errors[0].message)
  }

  return data?.product?.tags ?? []
}

async function fetchBadgeTags(productId: string): Promise<string[]> {
  try {
    return await fetchProductTagsFromStorefront(productId)
  } catch (e) {
    console.warn('auction.ts: tags unavailable, rendering without badges —', e)

    return []
  }
}

function pickFeatured(items: AuctionApiItem[]): AuctionApiItem | null {
  const now = Date.now()

  return (
    items
      .filter((item) => new Date(item.end_date).getTime() > now)
      .sort((a, b) => new Date(a.end_date).getTime() - new Date(b.end_date).getTime())[0] ?? null
  )
}

export async function fetchFeaturedAuction(): Promise<Auction | null> {
  const item = pickFeatured(await fetchAuctionsFromProxy())

  if (!item) {
    return null
  }

  return toAuction(item, await fetchBadgeTags(item.shopify_product_id))
}

export default () => ({
  auction: null as Auction | null,
  countdown: '',
  hasEnded: false,
  loading: true,
  error: false,
  _timer: null as number | null,

  async init() {
    try {
      this.auction = await fetchFeaturedAuction()

      if (this.auction) {
        this._startCountdown()
      }
    } catch (e) {
      this.error = true
      console.error('auction.ts:', e)
    } finally {
      this.loading = false
    }
  },

  destroy() {
    this._stopCountdown()
  },

  _startCountdown() {
    const tick = () => {
      const remaining = (this.auction as Auction).endsAt - Date.now()
      this.countdown = formatCountdown(remaining)

      if (remaining <= 0) {
        this.hasEnded = true
        this._stopCountdown()
      }
    }

    tick()
    this._timer = window.setInterval(tick, COUNTDOWN_INTERVAL)
  },

  _stopCountdown() {
    if (this._timer !== null) {
      window.clearInterval(this._timer)
      this._timer = null
    }
  },
})
